import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mic, MicOff, PhoneOff, AlertCircle, Users, User, RefreshCw } from 'lucide-react';
import { AdvisorAvatar } from './AdvisorAvatar';
import { useVoiceBoardroom } from '../hooks/useVoiceBoardroom';

interface Participant {
  id: string;
  name: string;
  title: string;
  description?: string;
  personality_prompt?: string;
  elevenlabs_agent_id?: string;
  gender?: string;
  is_default?: boolean;
  voice_settings?: any;
}

interface VoiceCallInterfaceProps {
  show: boolean;
  participants: Participant[];
  conversationId: string;
  userId: string;
  userName: string;
  isRecording: boolean;
  onToggleRecording: () => void;
  onEndCall: () => void;
}

export const VoiceCallInterface: React.FC<VoiceCallInterfaceProps> = ({
  show,
  participants,
  conversationId,
  userId,
  userName,
  onEndCall
}) => {
  const {
    startSession,
    endSession,
    isConnecting,
    isConnected,
    error,
    currentSpeaker,
    transcript,
    setMicMuted,
    clearError,
    getSessionInfo
  } = useVoiceBoardroom();

  const [isMuted, setIsMuted] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const startAttemptedRef = useRef(false);
  const startTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isGroupCall = participants.length > 1;

  // Start voice session when interface is shown (with delay to avoid React StrictMode issues)
  useEffect(() => {
    if (show && participants.length > 0 && !sessionStarted && !startAttemptedRef.current) {
      console.log('🚀 Scheduling voice session start...');
      
      // Mark as attempted immediately to prevent multiple schedules
      startAttemptedRef.current = true;
      setSessionStarted(true);
      
      // Add a small delay to avoid React StrictMode double-execution issues
      startTimeoutRef.current = setTimeout(() => {
        // Double-check conditions before starting
        if (show && participants.length > 0 && !isConnecting && !isConnected) {
          console.log('🎤 Starting voice session after delay...');
          startSession(participants, userName, conversationId);
        } else {
          console.log('🚫 Skipping voice session start - conditions changed');
          startAttemptedRef.current = false;
          setSessionStarted(false);
        }
      }, 1000); // 1 second delay
    }
    
    // Cleanup timeout if component unmounts or conditions change
    return () => {
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
        startTimeoutRef.current = null;
      }
    };
  }, [show, participants, sessionStarted, isConnecting, isConnected, startSession, userName, conversationId]);

  // Clean up when component unmounts or is hidden
  useEffect(() => {
    return () => {
      if (sessionStarted) {
        console.log('🧹 Cleaning up voice session on unmount...');
        endSession();
      }
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
      }
    };
  }, [endSession, sessionStarted]);

  // Handle mute/unmute
  const handleToggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    setMicMuted(newMutedState);
  };

  const handleEndCall = () => {
    // Clear any pending start timeout
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    
    if (sessionStarted) {
      endSession();
    }
    setSessionStarted(false);
    startAttemptedRef.current = false;
    onEndCall();
  };

  const handleRetry = () => {
    clearError();
    setSessionStarted(false);
    startAttemptedRef.current = false;
    
    // Clear any existing timeout
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    
    // Schedule a new attempt
    setTimeout(() => {
      if (show && participants.length > 0) {
        startAttemptedRef.current = true;
        setSessionStarted(true);
        startSession(participants, userName, conversationId);
      }
    }, 500);
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-white/10">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            {isGroupCall ? <Users className="w-5 h-5" /> : <User className="w-5 h-5" />}
            {isGroupCall 
              ? `Group Voice Call (${participants.length} advisors)`
              : `Voice Call with ${participants[0]?.name || 'Advisor'}`
            }
          </h2>
          {isGroupCall && (
            <p className="text-white/60 text-sm mt-1">
              {participants.map(p => p.name).join(', ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <div className={`w-2 h-2 rounded-full ${
            isConnected ? 'bg-green-400 animate-pulse' :
            isConnecting ? 'bg-yellow-400 animate-pulse' :
            error ? 'bg-red-400' : 'bg-gray-400'
          }`}></div>
          {error ? 'Error' : isConnected ? 'Live' : isConnecting ? 'Connecting...' : 'Starting...'}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        {error ? (
          // Error State
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-4">Voice Call Error</h3>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
            <div className="space-x-4">
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-purple-500 text-white hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={handleEndCall}
                className="px-6 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
              >
                End Call
              </button>
            </div>
          </div>
        ) : isConnecting ? (
          // Connecting State
          <div className="text-center">
            <div className="w-16 h-16 rounded-full border-4 border-orange-400/20 border-t-orange-400 animate-spin mx-auto mb-6"></div>
            <h3 className="text-xl font-bold text-white mb-4">Connecting to Voice Call</h3>
            <p className="text-white/70">
              {isGroupCall 
                ? `Starting group session with ${participants.map(p => p.name).join(', ')}...`
                : `Connecting to ${participants[0]?.name}...`
              }
            </p>
          </div>
        ) : (
          // Active Voice Call State
          <div className="text-center max-w-4xl mx-auto">
            {/* Advisor Display */}
            {isGroupCall ? (
              <div className="mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {participants.map((advisor) => (
                    <motion.div
                      key={advisor.id}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                        currentSpeaker === advisor.name
                          ? 'border-orange-400 bg-orange-500/20 scale-105'
                          : 'border-white/20 bg-white/5'
                      }`}
                      animate={{
                        scale: currentSpeaker === advisor.name ? 1.05 : 1
                      }}
                    >
                      <AdvisorAvatar name={advisor.name} size="large" className="mx-auto mb-2" />
                      <h4 className="text-white font-semibold text-sm">{advisor.name}</h4>
                      <p className="text-white/60 text-xs">{advisor.title}</p>
                      {currentSpeaker === advisor.name && (
                        <div className="flex items-center justify-center gap-1 mt-2">
                          {[1, 2, 3].map((i) => (
                            <div
                              key={i}
                              className="w-1 h-3 bg-orange-400 rounded-full animate-pulse"
                              style={{ animationDelay: `${i * 0.2}s` }}
                            />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Live Group Voice Call
                </h3>
                <p className="text-white/70 mb-4">
                  {currentSpeaker 
                    ? `${currentSpeaker} is speaking...` 
                    : 'Start speaking to begin the conversation!'
                  }
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <motion.div
                  animate={{
                    scale: currentSpeaker === participants[0]?.name ? 1.1 : 1
                  }}
                  className={`inline-block p-6 rounded-full ${
                    currentSpeaker === participants[0]?.name 
                      ? 'bg-orange-500/20 border-2 border-orange-400' 
                      : 'bg-white/5 border-2 border-white/20'
                  } transition-all duration-300`}
                >
                  <AdvisorAvatar 
                    name={participants[0]?.name || 'Advisor'} 
                    size="large" 
                    className="w-24 h-24"
                  />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-2 mt-4">
                  Live Call with {participants[0]?.name}
                </h3>
                <p className="text-white/70 mb-6">
                  {currentSpeaker === participants[0]?.name 
                    ? `${participants[0]?.name} is speaking...`
                    : 'Voice call is live. Start speaking!'
                  }
                </p>
                {currentSpeaker === participants[0]?.name && (
                  <div className="flex items-center justify-center gap-1 mb-4">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="w-2 h-4 bg-orange-400 rounded-full animate-pulse"
                        style={{ animationDelay: `${i * 0.2}s` }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Status Information */}
            <div className="bg-white/5 rounded-xl p-4 max-w-md mx-auto">
              <h4 className="text-white font-semibold mb-2">Voice Call Active</h4>
              <div className="space-y-2 text-left text-sm text-white/70">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>Live voice conversation in progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span>{isMuted ? 'Microphone muted' : 'Microphone active'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <span>{transcript.length} messages exchanged</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 p-6 border-t border-white/10">
        <button
          onClick={handleToggleMute}
          disabled={!isConnected}
          className={`p-4 rounded-full transition-all duration-300 ${
            !isConnected
              ? 'bg-white/5 text-white/30 cursor-not-allowed'
              : isMuted 
              ? 'bg-red-500 hover:bg-red-400 text-white' 
              : 'bg-white/10 hover:bg-white/20 text-white'
          }`}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </button>
        
        <button
          onClick={handleEndCall}
          className="p-4 rounded-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white transition-all duration-300"
          title="End Call"
        >
          <PhoneOff className="w-6 h-6" />
        </button>
      </div>
    </motion.div>
  );
};